import { useState } from 'react';
import { Action, Subject } from '@erp/authz';
import { workbookSourceSchema, type WorkbookSource } from '@erp/shared-types';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCan } from '@/hooks/useCan';
import type { WorkbookEditor } from './useWorkbookEditor';

export function WorkbookForecastSource({
  source,
  editor,
}: {
  source: Extract<WorkbookSource, { kind: 'forecast-snapshots' }>;
  editor: WorkbookEditor;
}) {
  const gt = useGT();
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const [runId, setRunId] = useState(source.runId ?? '');
  const [from, setFrom] = useState(source.asOfFrom ?? '');
  const [through, setThrough] = useState(source.asOfThrough ?? '');
  const [invalid, setInvalid] = useState(false);
  const apply = () => {
    const parsed = workbookSourceSchema.safeParse({
      ...source,
      runId: runId.trim() || undefined,
      asOfFrom: from || undefined,
      asOfThrough: through || undefined,
    });
    if (!parsed.success || (from && through && from > through)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    editor.edits.workbook.update((definition) => ({
      ...definition,
      sources: definition.sources.map((candidate) =>
        candidate.id === source.id ? parsed.data : candidate,
      ),
    }));
  };
  return (
    <div className="w-full space-y-2">
      <p>
        <T context="workbook forecast source origin">
          Current and past forecast snapshots, including archived runs.
          Aggregate actuals to the forecast period before joining on product key
          and warehouse ID.
        </T>
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="space-y-1">
          <span>
            <T context="workbook forecast run filter">Run ID (optional)</T>
          </span>
          <Input
            value={runId}
            disabled={!canUpdate}
            onChange={(event) => setRunId(event.target.value)}
            placeholder={gt('All runs', {
              context: 'workbook forecast run filter placeholder',
            })}
          />
        </label>
        <label className="space-y-1">
          <span>
            <T context="workbook forecast start date filter">As of, from</T>
          </span>
          <Input
            type="date"
            value={from}
            disabled={!canUpdate}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className="space-y-1">
          <span>
            <T context="workbook forecast end date filter">As of, through</T>
          </span>
          <Input
            type="date"
            value={through}
            disabled={!canUpdate}
            onChange={(event) => setThrough(event.target.value)}
          />
        </label>
        <Button
          size="sm"
          variant="outline"
          disabled={!canUpdate}
          onClick={apply}
        >
          <T context="workbook forecast apply filters">Apply filters</T>
        </Button>
      </div>
      {invalid && (
        <p role="alert" className="text-destructive">
          <T context="workbook forecast invalid filters">
            Enter a valid run ID and a date range whose end is on or after its
            start.
          </T>
        </p>
      )}
    </div>
  );
}
